import { Injectable, signal, Signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  tap,
  throwError,
  timeout} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  FLOW_GATEWAY,
  IWHFlowLoginDTO,
  IWHLoginDTO,
  WHDomEventService,
  WHJobRequirementsDataService,
  WHLoginDataService,
  WHTeamDataService,
  noWhitespaceValidator,
  IWHEquipmentJobRequirementsDTO,
  IWHJobRequirementsDTO,
  IWHJRParamModel,
  WHToastService,
} from '@workheld/workheld-shared-lib';

export class JobRequirementEntity {
  _jobRequirements: BehaviorSubject<JobRequirementsDOM>;
  jobRequirements$: Observable<JobRequirementsDOM>;
  _allActiveJobRequirements: BehaviorSubject<IWHJobRequirementsDTO[]>;
  allActiveJobRequirements$: Observable<IWHJobRequirementsDTO[]>;
  _jobRequirementsFiltered: BehaviorSubject<IWHJobRequirementsDTO[]>;
  jobRequirementsFiltered$: Observable<IWHJobRequirementsDTO[]>;
  _jobRequirement: BehaviorSubject<IWHJobRequirementsDTO>;
  jobRequirement$: Observable<any>;
  _equipmentJobRequirements: BehaviorSubject<IWHEquipmentJobRequirementsDTO[]>;
  equipmentJobRequirements$: Observable<any>;
  supervisors: Signal<IWHFlowLoginDTO[]>;
  _loading: BehaviorSubject<boolean>;
  loading$: Observable<boolean>;
  constructor() {
    this._jobRequirements = new BehaviorSubject<JobRequirementsDOM>(null);
    this._equipmentJobRequirements = new BehaviorSubject<
      IWHEquipmentJobRequirementsDTO[]
    >(null);
    this._allActiveJobRequirements = new BehaviorSubject<
      IWHJobRequirementsDTO[]
    >(null);
    this._jobRequirementsFiltered = new BehaviorSubject<
      IWHJobRequirementsDTO[]
    >(null);
    this._jobRequirement = new BehaviorSubject<any>(null);
    this._loading = new BehaviorSubject<boolean>(false);
    this.jobRequirements$ = this._jobRequirements.asObservable();
    this.supervisors = signal<IWHFlowLoginDTO[]>(null);
    this.equipmentJobRequirements$ =
      this._equipmentJobRequirements.asObservable();
    this.allActiveJobRequirements$ =
      this._allActiveJobRequirements.asObservable();
    this.jobRequirementsFiltered$ =
      this._jobRequirementsFiltered.asObservable();
    this.jobRequirement$ = this._jobRequirement.asObservable();
    this.loading$ = this._loading.asObservable();
  }
}

export enum COMPETENCE_TYPES {
  skills = 'SKILLS',
  qualifications = 'QUALIFICATIONS'}

export enum REQUIREMENT_TYPES {
  mustHave = 'MUST_HAVE',
  shouldHave = 'SHOULD_HAVE',
  couldHave = 'COULD_HAVE'}
export class JobRequirementsDOM {
  data: IWHJobRequirementsDTO[];
  numberOfItems: number;
  numberOfItemsTotal: number;
  numberOfPages: number;
  page: number;
  pageSize: number;
}
@Injectable({
  providedIn: 'root'})
export class JobRrequirementsDataService {
  public jrData = new JobRequirementEntity();

  loginsEntity = {
    logins: signal<IWHLoginDTO[]>([]),
    loading: signal<boolean>(false)};
  public jobRequirementStatuses = [
    { key: 'ACTIVE', translationKey: 'material.enum.status.ACTIVE' },
    { key: 'INACTIVE', translationKey: 'material.enum.status.INACTIVE' },
  ];
  constructor(
    private readonly jrService: WHJobRequirementsDataService,
    private readonly domEventService: WHDomEventService,
    private readonly ngxToastrService: WHToastService,
    private readonly teamService: WHTeamDataService,
    private readonly loginService: WHLoginDataService,
  ) {}

  // with no pagination
  async getAllActiveJobRequirements(params?: any): Promise<void> {
    if (!params?.teamId) {
      const teamId =
        await this.domEventService?.domEvents$?.getValue()?.payload?.id;

      if (teamId) {
        params = { ...params, teamId };
      }
    }
    this.jrData._loading.next(true);
    this.jrService
      .getAllActiveJobRequirements(
        { apiUrl: environment.apiUrl + FLOW_GATEWAY },
        params,
      )
      .pipe(
        map((jobRequirements: any) => {
          this.jrData._allActiveJobRequirements.next(jobRequirements);
          this.jrData._loading.next(false);
        }),
        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(err);
        }),
      )
      .subscribe();
  }

  getJobRequirementsFiltered(payload: string[], params?: IWHJRParamModel) {
    this.jrData._loading.next(true);
    return this.jrService
      .getJobRequirementsFiltered(
        { apiUrl: environment.apiUrl + FLOW_GATEWAY },
        payload,
        params,
      )
      .pipe(
        tap(() => this.jrData._loading.next(false)),
        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(err);
        }),
      );
  }

  async getJobRequirementsWithoutTeam(params: any): Promise<void> {
    this.jrData._loading.next(true);
    this.jrService
      .getJobRequirements({ apiUrl: environment.apiUrl + FLOW_GATEWAY }, params)
      .pipe(
        map((jobRequirements: JobRequirementsDOM) => {
          this.jrData._jobRequirements.next(jobRequirements);
          this.jrData._loading.next(false);
        }),
        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(err);
        }),
      )
      .subscribe();
  }

  getJobRequirements(
    params: any,
    withDetails: boolean = false,
  ): Observable<JobRequirementsDOM> {
    this.jrData._loading.next(true);
    return this.jrService
      .getJobRequirements({ apiUrl: environment.apiUrl + FLOW_GATEWAY }, params)
      .pipe(
        tap((jobRequirements: JobRequirementsDOM) => {
          if (withDetails) {
            // Synchronously enrich with cached team names before emitting
            const teams = this.teamService.teams$.getValue();
            const teamsAvailable = Array.isArray(teams) && teams.length > 0;
            if (teamsAvailable) {
              jobRequirements.data = jobRequirements.data.map((jr) => {
                const team = teams.find((d) => d.id === jr.teamId);
                return { ...jr, teamName: team ? team.name : '' };
              });
            }

            // Synchronously enrich with cached supervisor names before emitting
            const existingSupervisors = this.loginsEntity.logins();
            if (existingSupervisors?.length > 0) {
              jobRequirements.data = jobRequirements.data.map((jr) => {
                const sup = existingSupervisors.find(
                  (s) => s.id === jr.supervisorId,
                );
                return {
                  ...jr,
                  supervisorName: sup
                    ? sup.firstname + ' ' + sup.lastname
                    : jr.supervisorName || ''};
              });
            }
          }

          this.jrData._jobRequirements.next(jobRequirements);
          this.jrData._loading.next(false);

          if (withDetails) {
            // Skills, qualifications, and actualStaffNumber are now included
            // directly in the list API response (JobRequirementDetailDto).
            // Supervisor names and uncached team names still need client-side
            // enrichment.
            this.getSupervisors(
              jobRequirements?.data
                ?.map((jr) => jr.supervisorId)
                .filter((id, index, self) => id && self.indexOf(id) === index),
            );

            const teams = this.teamService.teams$.getValue();
            const teamsAvailable = Array.isArray(teams) && teams.length > 0;
            if (!teamsAvailable) {
              this.getTeamsByJobRequirementId(
                jobRequirements.data.map((jr) => jr.id),
              );
            }
          }
        }),

        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(() => err);
        }),
      );
  }

  getJobRequirementDetails(jrIds: string[]): void {
    this.getActualStaffNumber(jrIds);
    this.getSkillsByJobRequirementId(jrIds);
    this.getQualificationsByJobRequirementId(jrIds);
    this.getTeamsByJobRequirementId(jrIds);
  }

  async getTeamsByJobRequirementId(jrIds: string[]): Promise<void> {
    // Skip if team names were already resolved synchronously
    const currentData = this.jrData._jobRequirements?.getValue();
    const alreadyEnriched = currentData?.data?.every(
      (jr: any) => jr.teamName !== undefined,
    );
    if (alreadyEnriched) return;

    const teams = await firstValueFrom(
      this.teamService.teams$.pipe(
        filter((d) => Array.isArray(d) && d.length > 0),
        timeout(60000), // 60s max wait
      ),
    );
    if (teams && teams.length > 0) {
      const latestData = this.jrData._jobRequirements?.getValue();

      // set teamName from teamId
      if (latestData?.data) {
        const updatedData = { ...latestData };
        updatedData.data = updatedData.data.map((jr) => {
          const team = teams.find((d) => d.id === jr.teamId);
          return { ...jr, teamName: team ? team.name : '' };
        });
        this.jrData._jobRequirements.next(updatedData);
      }
    }
  }

  getSupervisors(supervisorIds?: string[]) {
    const existingSupervisors = this.loginsEntity.logins();
    this.setSupervisors(existingSupervisors);
    // set current
    const filteredupervisorIds = supervisorIds?.filter(
      (id) => !existingSupervisors?.some((sup) => sup.id === id),
    );

    if (!filteredupervisorIds?.length) {
      return;
    }

    const requests = filteredupervisorIds.map((userId) =>
      this.loginService.getFlowLoginDTOById({
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        userId}),
    );

    forkJoin(requests).subscribe({
      next: (supervisors) => {
        if (supervisors && supervisors?.length > 0) {
          this.loginsEntity.logins.set([
            ...this.loginsEntity.logins(),
            ...supervisors,
          ]);

          this.setSupervisors(this.loginsEntity.logins());
        }
      },
      error: (error) => {
        console.error('Error fetching supervisors:', error);
      }});
  }

  setSupervisors(supervisors: IWHLoginDTO[]) {
    const currentData = this.jrData._jobRequirements?.getValue();
    if (currentData?.data) {
      const updatedData = { ...currentData };
      updatedData.data = updatedData.data.map((jr) => {
        const sup = supervisors.find((s) => s.id === jr.supervisorId);
        return {
          ...jr,
          supervisorName: sup ? sup.firstname + ' ' + sup.lastname : ''};
      });
      this.jrData._jobRequirements.next(updatedData);
    }
  }

  getQualificationsByJobRequirementId(jrIds: string[]) {
    const requests = jrIds.map((id) =>
      this.jrService.getQualificationsByJobRequirementId({
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        id}),
    );

    forkJoin(requests).subscribe({
      next: (qualifications) => {
        const currentData = this.jrData._jobRequirements?.getValue();
        if (currentData?.data) {
          const updatedData = { ...currentData };
          updatedData.data = updatedData.data.map((jr, index) => ({
            ...jr,
            qualifications: qualifications[index]}));
          this.jrData._jobRequirements.next(updatedData);
        }
      },
      error: (error) => {
        console.error('Error fetching qualifications:', error);
      }});
  }

  getSkillsByJobRequirementId(jrIds: string[]) {
    const requests = jrIds.map((id) =>
      this.jrService.getSkillsByJobRequirementId({
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        id}),
    );

    forkJoin(requests).subscribe({
      next: (skills) => {
        const currentData = this.jrData._jobRequirements?.getValue();
        if (currentData?.data) {
          const updatedData = { ...currentData };
          updatedData.data = updatedData.data.map((jr, index) => ({
            ...jr,
            skills: skills[index]}));
          this.jrData._jobRequirements.next(updatedData);
        }
      },
      error: (error) => {
        console.error('Error fetching skills:', error);
      }});
  }

  getActualStaffNumber(jrIds: string[]): void {
    const requests = jrIds.map((id) =>
      this.jrService.getWorkerCountByJobRequirementId({
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        id}),
    );

    forkJoin(requests).subscribe({
      next: (counts) => {
        const currentData = this.jrData._jobRequirements?.getValue();
        if (currentData?.data) {
          const updatedData = { ...currentData };
          updatedData.data = updatedData.data.map((jr, index) => ({
            ...jr,
            actualStaffNumber: counts[index]}));
          this.jrData._jobRequirements.next(updatedData);
        }
      },
      error: (error) => {
        console.error('Error fetching worker counts:', error);
      }});
  }

  getJobRequirement(id: string): void {
    this.jrData._loading.next(true);
    this.jrService
      .getJobRequirementById({ apiUrl: environment.apiUrl + FLOW_GATEWAY, id })
      .pipe(
        map((jobRequirement: any) => {
          this.jrData._jobRequirement.next(jobRequirement);
          this.jrData._loading.next(false);
        }),
        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(err);
        }),
      )
      .subscribe();
  }

  getEquipmentJobRequirements(equipmentId: string, page: number): void {
    this.jrData._loading.next(true);
    this.jrService
      .getEquipmentJobRequirements(
        { apiUrl: environment.apiUrl + FLOW_GATEWAY, equipmentId: equipmentId },
        { size: 5, page },
      )
      .pipe(
        map((jobRequirements) => {
          this.jrData._equipmentJobRequirements.next(jobRequirements);
          this.jrData._loading.next(false);
        }),
        catchError((err) => {
          this.jrData._loading.next(false);
          return throwError(err);
        }),
      )
      .subscribe();
  }

  upsertEquipmentJobRequirements(equipmentId, payload) {
    this.jrData._loading.next(true);

    return this.jrService
      .upsertJobRequirementToEquipment(
        {
          apiUrl: environment.apiUrl + FLOW_GATEWAY,
          equipmentId: equipmentId},
        payload,
      )
      .pipe(
        tap(() => {
          this.jrData._loading.next(false);

          if (payload.id) {
            this.ngxToastrService.success('jobprofilesequipment.ui.updatesuccess.notification');
          } else {
            this.ngxToastrService.success('jobprofilesequipment.ui.assignsuccess.notification');
          }
        }),
      );
  }

  createJobRequirement(payload) {
    this.jrData._loading.next(true);
    return this.jrService.addJobRequirements(
      { apiUrl: environment.apiUrl + FLOW_GATEWAY },
      payload,
    );
  }

  deleteQualification(id: string) {
    this.jrData._loading.next(true);
    return this.jrService.deleteJobRequirement({
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      id: id});
  }

  unassignJobRequirementFromEquipment(id: string) {
    this.jrData._loading.next(true);
    return this.jrService
      .unassingJobRequirementFromEquipment({
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        equipmentId: id})
      .pipe(
        tap(() => {
          this.jrData._loading.next(false);

          this.ngxToastrService.success('jobprofilesequipment.ui.unassignsuccess.notification');
        }),
      );
  }

  public initJobRequirementFormGroup(): FormGroup {
    return new FormBuilder().group({
      id: [null],
      extId: [null], //
      name: [null, [Validators.required, noWhitespaceValidator]], //
      role: [null, [Validators.required, noWhitespaceValidator]], //
      description: [null],
      organisationalStructure: [null],
      minimumStaffNumber: [
        0,
        [Validators.pattern('^[0-9]*$'), Validators.maxLength(5)],
      ], //
      teamId: [null, Validators.required], //
      supervisorId: [null, Validators.required],
      skills: [null], //
      qualification: [null],
      status: ['ACTIVE', Validators.required]});
  }

  public initParamsFormGroup() {
    return new FormBuilder().group({
      page: [0],
      sort: null,
      sortMode: null,
      term: [null],
      pageSize: [20],
      teamId: [null]});
  }

  public initCompetenceFormGroup(): FormGroup {
    return new FormBuilder().group({
      jobRequirementId: [null, Validators.required],
      competenceId: [null, Validators.required],
      requirementType: [null, Validators.required]});
  }
}
